import styled, { DefaultTheme } from 'styled-components';
import { Modal, Button } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
    border-radius: 12px;
    box-shadow: ${(props) =>
      getThemeValue(
        props,
        '0 8px 24px rgba(0, 0, 0, 0.4)',
        '0 4px 16px rgba(0, 0, 0, 0.12)'
      )};
    overflow: hidden;
  }

  .ant-modal-header {
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
    border-radius: 12px 12px 0 0;
    padding: 20px 24px;
  }

  .ant-modal-title {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
    font-size: 18px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .ant-modal-body {
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
    font-size: 15px;
    line-height: 1.6;
  }

  .ant-modal-footer {
    background: ${(props) => getThemeValue(props, '#21262d', '#fafafa')};
    border-top: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
    border-radius: 0 0 12px 12px;
    padding: 16px 24px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }

  .ant-modal-close {
    color: ${(props) => getThemeValue(props, '#8b949e', '#8c8c8c')} !important;
    top: 20px;
    right: 24px;
    width: 32px;
    height: 32px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
      background: ${(props) => getThemeValue(props, '#30363d', '#f0f0f0')} !important;
    }
  }

  .ant-modal-close-x {
    width: 22px;
    height: 22px;
    line-height: 22px;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const IconWrapper = styled.span<{ $colorScheme: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: ${(props) => {
    const colorMap: Record<string, string> = {
      red: '#ff4d4f',
      blue: '#1890ff',
      green: '#52c41a',
      orange: '#fa8c16',
      purple: '#722ed1',
    };
    return colorMap[props.$colorScheme] || colorMap.red;
  }};
`;

export const MessageContainer = styled.div`
  color: ${(props) => getThemeValue(props, '#c9d1d9', '#595959')};
  font-size: 15px;
  line-height: 1.6;
  word-wrap: break-word;
`;

export const StyledCancelButton = styled(Button)`
  background: ${(props) => getThemeValue(props, '#21262d', '#f5f5f5')} !important;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#d9d9d9')} !important;
  color: ${(props) => getThemeValue(props, '#c9d1d9', '#1a1a1a')} !important;
  border-radius: 8px !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  height: 40px !important;
  padding: 0 20px !important;
  transition: all 0.2s !important;

  &:hover {
    background: ${(props) => getThemeValue(props, '#30363d', '#e6e6e6')} !important;
    border-color: ${(props) => getThemeValue(props, '#434343', '#bfbfbf')} !important;
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  }

  &:focus {
    background: ${(props) => getThemeValue(props, '#21262d', '#f5f5f5')} !important;
    border-color: ${(props) => getThemeValue(props, '#30363d', '#d9d9d9')} !important;
    color: ${(props) => getThemeValue(props, '#c9d1d9', '#1a1a1a')} !important;
  }
`;

export const FooterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 24px;
`;

export const StyledConfirmButton = styled(Button)<{ $colorScheme: string }>`
  background: ${(props) => {
    const colorMap: Record<string, string> = {
      red: '#ff4d4f',
      blue: '#1890ff',
      green: '#52c41a',
      orange: '#fa8c16',
      purple: '#722ed1',
    };
    return colorMap[props.$colorScheme] || colorMap.red;
  }} !important;
  border: none !important;
  color: #ffffff !important;
  border-radius: 8px !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  height: 40px !important;
  padding: 0 20px !important;
  transition: all 0.2s !important;
  box-shadow: ${(props) => {
    const colorMap: Record<string, string> = {
      red: '0 2px 4px rgba(255, 77, 79, 0.3)',
      blue: '0 2px 4px rgba(24, 144, 255, 0.3)',
      green: '0 2px 4px rgba(82, 196, 26, 0.3)',
      orange: '0 2px 4px rgba(250, 140, 22, 0.3)',
      purple: '0 2px 4px rgba(114, 46, 209, 0.3)',
    };
    return colorMap[props.$colorScheme] || colorMap.red;
  }} !important;

  &:hover {
    background: ${(props) => {
      const colorMap: Record<string, string> = {
        red: '#ff7875',
        blue: '#40a9ff',
        green: '#73d13d',
        orange: '#ffa940',
        purple: '#9254de',
      };
      return colorMap[props.$colorScheme] || colorMap.red;
    }} !important;
    color: #ffffff !important;
    border-color: transparent !important;
    box-shadow: ${(props) => {
      const colorMap: Record<string, string> = {
        red: '0 4px 8px rgba(255, 77, 79, 0.4)',
        blue: '0 4px 8px rgba(24, 144, 255, 0.4)',
        green: '0 4px 8px rgba(82, 196, 26, 0.4)',
        orange: '0 4px 8px rgba(250, 140, 22, 0.4)',
        purple: '0 4px 8px rgba(114, 46, 209, 0.4)',
      };
      return colorMap[props.$colorScheme] || colorMap.red;
    }} !important;
  }

  &:focus {
    background: ${(props) => {
      const colorMap: Record<string, string> = {
        red: '#ff4d4f',
        blue: '#1890ff',
        green: '#52c41a',
        orange: '#fa8c16',
        purple: '#722ed1',
      };
      return colorMap[props.$colorScheme] || colorMap.red;
    }} !important;
    color: #ffffff !important;
    border-color: transparent !important;
  }
`;
